// src/components/TreasureMap.js
import React, { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const TreasureMap = ({
  topics = [],
  visibleTopics = [],
  completedTopics = [],
  targetSlug = null,
  subject,
  classTitle = "",
  subjectKey, // prefer this when provided
}) => {
  const [progress, setProgress] = useState({});
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const brightStar =
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbright_star.png?alt=media&token=b033c5b7-788e-401c-b650-98399c1e8551";
  const blankStar =
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fblank_star.png?alt=media&token=0dd0e339-3016-4f1c-8db2-eae91a16c21a";

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    let authUnsub = null;
    let dataUnsub = null;

    authUnsub = onAuthStateChanged(auth, async (currentUser) => {
      // cleanup any previous userData subscription
      if (dataUnsub) {
        dataUnsub();
        dataUnsub = null;
      }

      if (!currentUser) {
        setUser(null);
        setProgress({});
        return;
      }

      setUser(currentUser);

      // Prefer prop subjectKey; otherwise derive from USERS doc
      let derivedKey = subjectKey;
      if (!derivedKey) {
        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        const u = userSnap.data() || {};
        const state = String(u.state || "nc").toLowerCase();
        const grade = String((u.grade || "1st").split(",")[0].trim()).toLowerCase();
        derivedKey = `${state}_${grade}_${String(subject).toLowerCase()}`;
      }

      // Listen in realtime to userData for star updates
      const userDataRef = doc(db, "userData", currentUser.uid);
      dataUnsub = onSnapshot(userDataRef, (snap) => {
        const userData = snap.exists() ? snap.data() : {};
        const subjectProgress = userData[derivedKey] || {};

        const progressMap = {};
        topics.forEach((t) => {
          const entry = subjectProgress[t.slug] || {};
          const gameValue = entry?.game;
          const isGameComplete =
            typeof gameValue === "boolean" ? gameValue : Boolean(gameValue?.completed);
          const score =
            typeof gameValue === "object" && gameValue?.score ? gameValue.score : 0;

          progressMap[t.slug] = {
            video: Boolean(entry.video),
            music: Boolean(entry.music),
            game: isGameComplete,
            score,
          };
        });

        setProgress(progressMap);
      });
    });

    return () => {
      if (authUnsub) authUnsub();
      if (dataUnsub) dataUnsub();
    };
  }, [subject, topics, subjectKey]);

  const chestVariants = {
    initial: { opacity: 0, y: 20, scale: 1 },
    normal: { opacity: 1, y: 0, scale: 1 },
    selected: { opacity: 1, y: 0, scale: 1.4 },
  };

  const handleChestClick = (slug) => {
    setTimeout(() => {
      navigate(`/games/${subject}/${slug}`);
    }, 600);
  };

  // default visible when unknown
  const safeVisible = topics.map((_, i) => visibleTopics?.[i] ?? true);

  if (!topics.length) {
    return <p style={{ color: "#fff", textAlign: "center" }}>Loading topics…</p>;
  }

  return (
    <>
      <style>
        {`
          .completed-chest { animation: glow 2s infinite alternate; }
          @keyframes glow { 0% { filter: drop-shadow(0 0 5px gold); } 100% { filter: drop-shadow(0 0 15px gold); } }
          .sparkle { animation: sparkle-glow 2s infinite alternate; filter: drop-shadow(0 0 8px gold); }
          @keyframes sparkle-glow { from { filter: drop-shadow(0 0 4px gold); } to { filter: drop-shadow(0 0 12px gold); } }
          .shake { animation: shake 0.5s infinite; }
          @keyframes shake { 0% { transform: translateX(0); } 25% { transform: translateX(-5px); } 50% { transform: translateX(5px); } 75% { transform: translateX(-5px); } 100% { transform: translateX(0); } }
        `}
      </style>

      <div style={styles.mapContainer}>
        <div style={styles.titleBanner}>
          <h1 style={styles.titleText}>{classTitle}</h1>
        </div>

        {topics.map((topic, index) => {
          if (!safeVisible[index]) return null;

          const p = progress?.[topic.slug] || {};
          const videoComplete = p.video === true;
          const musicComplete = p.music === true;
          const gameComplete = p.game === true;
          const isCompleted = videoComplete && musicComplete && gameComplete;
          const isTarget = topic.slug === targetSlug;

          return (
            <div key={topic.slug || index} style={styles.chestWrapper}>
              <motion.button
                onClick={() => handleChestClick(topic.slug)}
                variants={chestVariants}
                initial="initial"
                animate={isTarget ? "selected" : "normal"}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={styles.chestButton}
              >
                <img
                  src={`/assets/${isCompleted ? "chest-open.png" : "chest-closed.png"}`}
                  alt="Chest"
                  style={{ width: 80 }}
                  className={isCompleted ? "sparkle" : ""}
                />
                <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 5 }}>
                  <img src={videoComplete ? brightStar : blankStar} alt="Video Star" style={{ width: 20, height: 20 }} />
                  <img src={musicComplete ? brightStar : blankStar} alt="Music Star" style={{ width: 20, height: 20 }} />
                  <img src={gameComplete ? brightStar : blankStar} alt="Game Star" style={{ width: 20, height: 20 }} />
                </div>
              </motion.button>

              <p style={styles.chestLabel}>{topic.title}</p>
            </div>
          );
        })}
      </div>
    </>
  );
};

const styles = {
  mapContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "60px",
    paddingTop: "200px",
    paddingRight: "60px",
    paddingBottom: "60px",
    paddingLeft: "60px",
    backgroundImage: `url("/assets/map-background.jpg")`,
    backgroundSize: "cover",
    minHeight: "100vh",
  },
  titleBanner: {
    position: "absolute",
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    padding: "10px 20px",
    borderRadius: "12px",
    zIndex: 15,
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
  },
  titleText: {
    fontSize: "22px",
    fontWeight: "bold",
    color: "#333",
    margin: 0,
    textAlign: "center",
  },
  chestWrapper: {
    position: "relative",
    textAlign: "center",
    width: 100,
  },
  chestButton: {
    fontSize: "50px",
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  chestLabel: {
    marginTop: "10px",
    fontWeight: "bold",
    color: "#fff",
    textShadow: "1px 1px 2px #000",
  },
};

export default TreasureMap;
